import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { ProductVariant } from '../../../../../shared/models/product.model';
import { IconComponent } from '../../../../../shared/ui/icon/icon.component';
import { InnerZoomDirective } from '../../../../../shared/directives/inner-zoom.directive';
import { RotateCcw, ZoomIn, LucideIconData } from 'lucide-angular';

@Component({
  selector: 'app-image-gallery',
  imports: [IconComponent, InnerZoomDirective],
  templateUrl: './image-gallery.component.html',
  styleUrl: './image-gallery.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ImageGalleryComponent {
  // Inputs
  images = input.required<string[]>();
  productName = input.required<string>();
  variant = input<ProductVariant>('non-foil');
  isSingle = input(true);
  hasDoubleFace = input(false);
  backImageUrl = input<string | undefined>(undefined);

  // Outputs
  zoomClick = output<number>();

  // Icons
  protected readonly rotateIcon: LucideIconData = RotateCcw;
  protected readonly zoomIcon: LucideIconData = ZoomIn;

  // State
  protected readonly selectedIndex = signal(0);
  protected readonly isFlipped = signal(false);

  // Computed
  protected readonly selectedImage = computed(() => this.images()[this.selectedIndex()] ?? '');
  protected readonly hasMultipleImages = computed(() => this.images().length > 1);

  // Touch tracking
  private touchStartX = 0;

  @ViewChild('thumbStrip') private thumbStripRef!: ElementRef<HTMLElement>;

  selectImage(index: number): void {
    this.selectedIndex.set(index);
    this.isFlipped.set(false);
  }

  onImageClick(): void {
    if (this.hasDoubleFace() && this.backImageUrl()) {
      this.toggleFlip();
    } else {
      this.zoomClick.emit(this.selectedIndex());
    }
  }

  onZoomClick(): void {
    this.zoomClick.emit(this.selectedIndex());
  }

  toggleFlip(): void {
    this.isFlipped.update((v) => !v);
  }

  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.touches[0].clientX;
  }

  onTouchEnd(event: TouchEvent): void {
    const delta = event.changedTouches[0].clientX - this.touchStartX;
    const threshold = 50;
    const images = this.images();
    if (Math.abs(delta) < threshold || images.length <= 1) return;

    const current = this.selectedIndex();
    if (delta < 0 && current < images.length - 1) {
      this.selectedIndex.set(current + 1);
    } else if (delta > 0 && current > 0) {
      this.selectedIndex.set(current - 1);
    }
    this.isFlipped.set(false);
  }
}
